/**
 * @jsxRuntime classic
 * @jsx jsx
 *
 * Public entry point for the embeddable Graph Explorer used by the TWG microsite.
 *
 * Wraps the dtac `simple-home` graph explorer in a sized container so it can be
 * rendered:
 *   - Inline inside an 800px section on the landing page, and
 *   - Inside a near-fullscreen overlay when the user expands it.
 *
 * The underlying explorer renders the same `ObjectsVisualizer` and uses the same
 * Cypher / network calls as the original dtac implementation.
 *
 * State isolation:
 *   The explorer is wrapped in a scoped Jotai `<Provider>` whose `store` is
 *   recreated whenever the microsite's `cloudIdAtom` changes. This gives every
 *   atom inside the explorer a fresh value (a clean slate) on each tenant
 *   switch — no per-atom reset bookkeeping required. The `cloudIdAtom` value
 *   from the outer/default store is also seeded into the inner store via
 *   `useHydrateAtoms` so explorer code reads the right tenant immediately.
 */
import { useMemo, type ReactNode } from 'react';

import { jsx } from '@compiled/react';
import { createStore, Provider, useAtomValue } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';

import { cloudIdAtom } from '../ui/state';

import GraphExplorerPage from './ui/pages/graph-explorer/simple-home';

// Use a global stylesheet rule (compiled css supports nested selectors) so
// the underlying GraphExplorerPage's hardcoded `calc(100vh - 56px)` height is
// overridden to fill its parent (modal body or 800px section) instead of the
// viewport.
const wrapperClass = 'twg-microsite-graph-explorer-wrapper';

const wrapperStyles = {
	className: wrapperClass,
	style: { width: '100%', height: '100%', overflow: 'hidden' as const },
};

/**
 * Seeds the inner (scoped) Jotai store with the current cloudId before
 * the explorer subtree mounts. `useHydrateAtoms` only writes the initial
 * values; on subsequent renders the Provider's `key` (cloudId) will have
 * caused a fresh store to be created, so each hydration writes into a
 * brand-new store.
 */
function HydrateInnerStore({
	cloudId,
	children,
}: {
	cloudId: string | null;
	children: ReactNode;
}): JSX.Element {
	useHydrateAtoms([[cloudIdAtom, cloudId]]);
	// Wrap in a fragment-equivalent div with display:contents so it doesn't
	// affect layout. Avoids needing an @jsxFrag pragma alongside the @jsx
	// pragma at the top of the file.
	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- display:contents to act as a fragment
		<div style={{ display: 'contents' }}>{children}</div>
	);
}

type GraphExplorerProps = {
	/**
	 * Optional override for the cloudId used by the explorer. When provided,
	 * this takes precedence over the microsite's outer `cloudIdAtom`. This
	 * exists so embeddings outside the microsite (for example dtac's
	 * `/simple-graph-explorer` page) can supply a cloudId without having to
	 * populate the microsite's auth-driven cloudIdAtom.
	 */
	cloudId?: string | null;
};

export function GraphExplorer({
	cloudId: cloudIdProp,
}: GraphExplorerProps = {}): JSX.Element | null {
	// Prefer the explicit prop (e.g. dtac's `/simple-graph-explorer` page)
	// otherwise fall back to the microsite's outer/default `cloudIdAtom`
	// (set by the microsite's auth flow / SiteSwitcher).
	// We always call the hook to satisfy rules-of-hooks; if the caller passed
	// a prop, the hook value is just ignored.
	const cloudIdFromAtom = useAtomValue(cloudIdAtom);
	const cloudId = cloudIdProp !== undefined ? cloudIdProp : cloudIdFromAtom;

	// `useMemo` keyed on cloudId guarantees a brand-new store reference each
	// time the cloudId changes. Combined with the `key` prop on Provider, this
	// forces React to unmount/remount the subtree against the new store.
	// `cloudId` is intentionally listed as a dependency even though the factory
	// doesn't reference it — the *change* in cloudId is the trigger for a new
	// store, not the value itself.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const store = useMemo(() => createStore(), [cloudId]);

	if (!cloudId) {
		// Avoid mounting the explorer with an unknown tenant — downstream
		// queries would fire with no X-Query-Context header.
		return null;
	}

	return (
		<div
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Need a class so the global styles below can target this wrapper specifically
			className={wrapperStyles.className}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Static container sizing; using inline styles to keep the embed self-contained
			style={wrapperStyles.style}
		>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, react/no-unknown-property, @atlaskit/ui-styling-standard/no-global-styles -- Need to override the underlying GraphExplorerPage's hardcoded height with a scoped global rule */}
			<style>{`.${wrapperClass} > div { height: 100% !important; max-height: 100% !important; }`}</style>
			<Provider key={cloudId} store={store}>
				<HydrateInnerStore cloudId={cloudId}>
					<GraphExplorerPage />
				</HydrateInnerStore>
			</Provider>
		</div>
	);
}

GraphExplorer;
